import { Button, Menu, MenuItem, Stack, Typography } from '@mui/material'
import debounce from 'lodash/debounce'
import { forwardRef, MouseEvent, useEffect, useRef, useState } from 'react'
import { UseFormSetValue } from 'react-hook-form'
import { Icons, SearchInputField, SearchInputFieldProps } from 'utoniq-ui'

import { OrderBy } from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { Form, useForm, UseFormProps, useTranslation } from '@/core'
import { Cell, CellFrame, Organism, OrganismFrame } from '@/core/architecture'
import { useAnalytics } from '@/lib/analytics'

export type SearchFormProps = {
  artistId: string
  onSubmit: (values: SearchFormValues) => Promise<void>
  onFocusInputSearch: () => void
  onBlurInputSearch: () => void
  sort: OrderBy
}

export type SearchFormValues = {
  searchTerm: string
  orderBy: OrderBy
}

const schema: UseFormProps['schema'] = o => ({
  searchTerm: o.string().allow('').optional(),
  orderBy: o.string().allow('').optional(),
})

const defaultValues: SearchFormValues = {
  searchTerm: '',
  orderBy: OrderBy.Desc,
}

export const SearchForm: Organism<SearchFormProps> = ({
  artistId,
  onSubmit,
  onFocusInputSearch,
  onBlurInputSearch,
  sort,
}) => {
  const { analytics } = useAnalytics()
  const { handleSubmit, register, watch, setValue } = useForm<SearchFormValues>(
    {
      schema,
      defaultValues,
    }
  )
  const handleChangeKeywordDebounce = useRef(
    debounce(
      handleSubmit(({ searchTerm }) =>
        onSubmit({ ...defaultValues, searchTerm })
      ),
      500
    )
  )

  return (
    <OrganismFrame>
      <Form onSubmit={handleSubmit(onSubmit)} sx={{ bgcolor: 'transparent' }}>
        <SearchInputCell
          {...register('searchTerm')}
          showClearButton={!!watch('searchTerm')}
          artistId={artistId}
          onChange={async event => {
            setValue('searchTerm', event.target.value)
            await handleChangeKeywordDebounce.current()
          }}
          onBlur={event => {
            const { onBlur } = register('searchTerm')
            onBlurInputSearch()
            return onBlur(event)
          }}
          textFieldProps={{
            onFocus() {
              analytics?.track('click_customerSearch', {
                applicationKey: config.applicationKey,
                artistId,
              })
              onFocusInputSearch()
            },
          }}
        />
        <FilterCell
          setValue={setValue}
          onChange={handleSubmit(onSubmit)}
          sort={sort}
        />
      </Form>
    </OrganismFrame>
  )
}

const SearchInputCell = forwardRef(
  (
    {
      showClearButton,
      artistId,
      ...props
    }: Omit<SearchInputFieldProps, 't'> & {
      showClearButton: boolean
      artistId: string
    },
    ref
  ) => {
    const { t } = useTranslation('consoleUsers')

    return (
      <CellFrame
        component={Stack}
        flexDirection="row"
        alignItems="center"
        width="100%"
      >
        <SearchInputField {...props} ref={ref} t={t} />
        {showClearButton && (
          <Button
            variant="text"
            sx={{ minWidth: 86 }}
            onClick={() => {
              if (props.onChange) {
                props?.onChange({ target: { value: '' }, type: '' })
              }
            }}
          >
            {t('cancel', 'キャンセル')}
          </Button>
        )}
      </CellFrame>
    )
  }
)

type FilterOptions = { label: string; value: OrderBy }[]

type FilterCellProps = {
  setValue: UseFormSetValue<SearchFormValues>
  onChange: () => void
  sort: OrderBy
}
const FilterCell: Cell<FilterCellProps> = ({ setValue, onChange, sort }) => {
  const { t } = useTranslation('consoleUsers')
  const filterOptions: FilterOptions = [
    { label: t('newestOrderDate', '注文日の新しい順'), value: OrderBy.Desc },
    { label: t('oldestOrderDate', '注文日の古い順'), value: OrderBy.Asc },
  ]
  // sort と filterOptions の value を比較して、一致するインデックスを取得
  const initialSelectedIndex = sort
    ? filterOptions.findIndex(option => option.value === sort)
    : 0
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedOption, setSelectedOption] = useState<FilterOptions[number]>(
    filterOptions[initialSelectedIndex]
  )
  const open = Boolean(anchorEl)

  const handleClickListItem = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuItemClick = (
    event: MouseEvent<HTMLElement>,
    option: FilterOptions[number]
  ) => {
    setSelectedOption(option)
    setValue('orderBy', option.value)
    onChange()
    setAnchorEl(null)
  }

  useEffect(() => {
    const UrlParams = `?sort=${selectedOption.value}`
    // React Routerのhistoryを使うとソート順を変えた際に、スクロール位置が一番上になるため、JavaScriptで直接historyを利用している
    window.history.replaceState(null, '', UrlParams)
  }, [selectedOption])

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <CellFrame sx={{ py: 1 }}>
      <Stack direction="row-reverse" spacing={2}>
        <Button variant="text" sx={{ mr: -2 }} onClick={handleClickListItem}>
          <Typography variant="caption" sx={{ mr: 2 }}>
            {selectedOption.label}
          </Typography>
          <Icons.DownArrow fontSize="xSmall" />
        </Button>
      </Stack>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {filterOptions.map(option => (
          <MenuItem
            key={option.value}
            selected={option.value === selectedOption.value}
            onClick={event => handleMenuItemClick(event, option)}
          >
            <Typography variant="body2">{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </CellFrame>
  )
}
